import { gdprCookies, gdpr } from '@bedrock-rdk/config';
import { storage, isStorageSupported, getHomeId } from '@bedrock-rdk/utils';
import {
  CALL_USER_CONSENTS_SUCCESS,
  FETCH_PROFILE_INFOS_SUCCESS,
  GENERATE_DEVICE_ID,
  RESET_DEVICE_DATA,
  RESTORE_DEVICE_DATA,
  SET_USER_CONSENTS,
  SYNCHRONIZE_USER_CONSENTS,
  UPDATE_DEVICE_ID,
  USER_RESTORE_PROFILE_UID,
  generateDeviceId,
  setUserConsentsOnApi,
  storageWriteFailure,
  storageSupportFailure,
  setPreviousProfileUID,
  SET_USER_UID,
} from '../logic/user/user.actions';
import { INITIALIZED, TRACK_PREFERENCES_CHANGED } from '../logic/player/player.constants';
import { updateSearchedItems } from '../logic/search/search.actions';
import { TRACK_KEYS } from '../modules/video/video.constants';
import {
  GET_SEARCHED_ITEM,
  SAVE_SEARCHED_ITEM,
  SEARCH_HISTORY_CAPACITY,
  SEARCH_HISTORY_KEY,
} from '../logic/search/search.constants';
import { INITIALIZE_APP_COMPLETED } from '../logic/app/app.actions';
import { CONFIG_INIT } from '../logic/app/app.config.actions';
import { getUid, shouldSynchronizeConsents } from '../logic/user/user.selectors';
import { CLEAR_USER_PROFILE, RENEW_TOKEN_SUCCESS, PAIRING_LOGOUT_SUCCESS } from '../logic/pairing/pairing.actions';
import { PAIRING_RESET } from '../logic/pairing/unpair.actions';
import { setPairingMethod } from '../modules/pairing/pairing.utils';
import { incrementAccountCompletionCounter } from '../logic/user/user.utils';
import { PAIRING_METHOD } from '../modules/pairing/pairing.constants';
import {
  getMultiProfilesGateAlwaysDisplayed,
  getPlayerTrackPreferencesFeature,
} from '../logic/features/features.selectors';
import { getCurrentSectionCode } from '../logic/layout/layout.selectors';
import { PERSISTENT } from '../logic/app/app.constants';

/**
 * Retrieve the value of the consent cookies, using the
 * mapped cookie name from the config (with the typos and all).
 *
 * @returns {Object}
 */
const getConsents = () =>
  gdprCookies.reduce((memo, cookieName) => {
    if (!cookieName) {
      return memo;
    }

    return {
      ...memo,
      [cookieName]: storage.getItem(cookieName),
    };
  }, {});

/**
 * Filter the cookies sent from the action, to keep only the ones
 * defined in the config (with the typos and all).
 *
 * @param {Object} action
 * @returns {Object}
 */
const getFilteredConsents = action =>
  Object.entries(action).reduce((memo, [cookieName, cookieValue]) => {
    // Do not include cookies that are not defined in the config
    if (!gdprCookies.includes(cookieName)) {
      return memo;
    }

    return { ...memo, [cookieName]: cookieValue };
  }, {});

const getUserId = store => {
  try {
    const {
      user: { profileUid, uid },
    } = store.getState();

    return profileUid || uid;
  } catch (error) {
    return null;
  }
};

const makeSynchronizeKey = uid => `${uid || 'anonymous'}.${PERSISTENT.SYNCHRONIZE_KEY}`;

const makePreferencesKey = (key, userProfile) => [userProfile, key].filter(Boolean).join('.');

const saveSearchedItem = (store, item) => {
  const id = getUserId(store);
  const section = getCurrentSectionCode(store.getState());

  if (id && section) {
    const history = storage.getItem(`${id + SEARCH_HISTORY_KEY}_${section}`);
    const parsedHistory = history ? JSON.parse(history) : [];
    parsedHistory.unshift(item);
    // remove duplicates
    const result = parsedHistory.filter((historyItem, index) => historyItem !== item || index === 0);

    try {
      storage.setItem(
        `${id + SEARCH_HISTORY_KEY}_${section}`,
        JSON.stringify(result.slice(0, SEARCH_HISTORY_CAPACITY)),
      );
    } catch (error) {
      store.dispatch(storageWriteFailure(error));
    }
  }
};

const getSearchedItems = store => {
  const id = getUserId(store);
  const section = getCurrentSectionCode(store.getState());

  try {
    return JSON.parse(storage.getItem(`${id + SEARCH_HISTORY_KEY}_${section}`)) || [];
  } catch (error) {
    return null;
  }
};

export default store => next => action => {
  const state = store.getState();
  let currentAction = { ...action };

  switch (currentAction.type) {
    case RESTORE_DEVICE_DATA: {
      currentAction[PERSISTENT.DEVICE_ID_KEY] = storage.getItem(PERSISTENT.DEVICE_ID_KEY);
      currentAction[PERSISTENT.SYNCHRONIZE_KEY] = storage.getItem(makeSynchronizeKey(getUid(state)));
      currentAction.homeId = getHomeId();

      currentAction = {
        ...currentAction,
      };

      break;
    }
    case CONFIG_INIT: {
      const consentVersion = storage.getItem(PERSISTENT.CONSENT_VERSION);

      currentAction = {
        ...currentAction,
        cookies: getConsents(),
        consentVersion: Number.isFinite(consentVersion) ? consentVersion : 0,
      };
      break;
    }
    case INITIALIZED: {
      incrementAccountCompletionCounter();

      const shouldTrackPreferencesAcrossDevices = getPlayerTrackPreferencesFeature(state);

      if (!shouldTrackPreferencesAcrossDevices) {
        const audioLanguage = storage.getItem(
          makePreferencesKey(TRACK_KEYS.PREFERRED_AUDIO_TRACK_LANGUAGE, state.user.profileUid),
        );
        const subtitlesLanguage = storage.getItem(
          makePreferencesKey(TRACK_KEYS.PREFERRED_SUBTITLE_TRACK_LANGUAGE, state.user.profileUid),
        );

        let subtitles = null;
        let audio = null;

        if (subtitlesLanguage) {
          subtitles = {
            language: subtitlesLanguage,
            role: 'standard',
          };
          if (subtitlesLanguage.language) {
            subtitles = {
              language: subtitlesLanguage.language,
              role: subtitlesLanguage.role || 'standard',
            };
          }
        }

        if (audioLanguage) {
          audio = {
            language: audioLanguage,
            audioDescription: false,
          };
          if (audioLanguage.language) {
            audio = {
              language: audioLanguage.language,
              audioDescription: audioLanguage.audioDescription || false,
            };
          }
        }

        currentAction = {
          ...currentAction,
          item: {
            ...currentAction.item,
            video: {
              ...currentAction.item.video,
              features: {
                ...currentAction.item.video.features,
                preferences: {
                  ...currentAction.item.video.features.preferences,
                  textTrack: subtitles,
                  audioTrack: audio || currentAction.item.video.features.preferences.audioTrack,
                },
              },
            },
          },
        };
      }

      break;
    }
    case PAIRING_RESET: {
      const uid = getUid(state);
      storage.setItem(makeSynchronizeKey(uid), false);
      break;
    }
    default:
      break;
  }

  next(currentAction);

  const uid = getUid(state);

  switch (currentAction.type) {
    case RESTORE_DEVICE_DATA: {
      try {
        isStorageSupported(storage);
      } catch (error) {
        store.dispatch(storageSupportFailure(error));
      }
      if (!currentAction.deviceId && !currentAction.homeId) {
        store.dispatch(generateDeviceId());
      }
      break;
    }

    case INITIALIZE_APP_COMPLETED:
    case SET_USER_UID: {
      const isSynchronized = storage.getItem(makeSynchronizeKey(currentAction.uid || uid));
      // Ensure we are not sending consent data on each login
      if (isSynchronized) {
        store.dispatch({ type: SYNCHRONIZE_USER_CONSENTS });
      }

      if (shouldSynchronizeConsents(isSynchronized, store.getState())) {
        const userConsents = getConsents();

        store.dispatch(setUserConsentsOnApi({ cookies: userConsents }));
      }
      break;
    }

    case RESET_DEVICE_DATA: {
      storage.clear();
      break;
    }

    case SET_USER_CONSENTS: {
      try {
        const consentCookies = getFilteredConsents(currentAction);
        const currentVersion = parseInt(gdpr.consentVersion, 10);

        storage.setItems({
          ...consentCookies,
          [PERSISTENT.CONSENT_VERSION]: Number.isFinite(currentVersion) ? currentVersion : 0,
          [makeSynchronizeKey(uid)]: false,
        });
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }

      break;
    }

    case GENERATE_DEVICE_ID: {
      try {
        storage.setItem(PERSISTENT.DEVICE_ID_KEY, currentAction.deviceId, false);
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }
      break;
    }

    case UPDATE_DEVICE_ID: {
      storage.setItem(PERSISTENT.DEVICE_ID_KEY, currentAction.deviceId, false);
      storage.setItem(makeSynchronizeKey(uid), false);
      break;
    }

    case CALL_USER_CONSENTS_SUCCESS: {
      try {
        storage.setItem(makeSynchronizeKey(uid), true);
        store.dispatch({ type: SYNCHRONIZE_USER_CONSENTS });
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }

      break;
    }

    case RENEW_TOKEN_SUCCESS: {
      try {
        const shouldDisplayProfilesGateOnNextVisit = getMultiProfilesGateAlwaysDisplayed(state);
        // NOTE: Since dev mode utilizes hot reload it's annoying to hit the profile selector each time we need to debug something.
        // If you need to disable this behavior, run an app in STAGING/PRODUCTION environment.
        // Also don't forget to clear "profileUid" cookie.
        if (!shouldDisplayProfilesGateOnNextVisit) {
          storage.setItem(`${uid}.${PERSISTENT.PROFILE_UID_KEY}`, currentAction.profileUid);
        }
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }
      break;
    }

    case CLEAR_USER_PROFILE: {
      try {
        storage.removeItem(`${uid}.${PERSISTENT.PROFILE_UID_KEY}`);
        storage.removeItem(`${uid}.${PERSISTENT.PROFILE_SECTION_KEY}`);
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }
      break;
    }

    case USER_RESTORE_PROFILE_UID: {
      const previousProfileUid = storage.getItem(PERSISTENT.PREVIOUS_PROFILE_KEY);

      if (previousProfileUid) {
        store.dispatch(setPreviousProfileUID(previousProfileUid));
      }

      break;
    }

    case FETCH_PROFILE_INFOS_SUCCESS: {
      const { profileUid, isKidsProfile } = currentAction;

      try {
        storage.setItem(PERSISTENT.PREVIOUS_PROFILE_KEY, profileUid, true);
        // NOTE: restored along with the profile, so the section is right before the first layout is requested
        if (!getMultiProfilesGateAlwaysDisplayed(state)) {
          storage.setItem(`${uid}.${PERSISTENT.PROFILE_SECTION_KEY}`, isKidsProfile ? 'kids' : 'main');
        }
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }
      break;
    }

    case PAIRING_LOGOUT_SUCCESS: {
      try {
        storage.removeItem(PERSISTENT.PREVIOUS_PROFILE_KEY);
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }
      break;
    }

    case TRACK_PREFERENCES_CHANGED: {
      try {
        const {
          player: { preferences },
          user: { profileUid },
        } = store.getState();

        if (preferences.audioTrack) {
          storage.setItem(
            makePreferencesKey(TRACK_KEYS.PREFERRED_AUDIO_TRACK_LANGUAGE, profileUid),
            preferences.audioTrack,
          );
        }
        storage.setItem(
          makePreferencesKey(TRACK_KEYS.PREFERRED_SUBTITLE_TRACK_LANGUAGE, profileUid),
          preferences.textTrack,
        );
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }
      break;
    }

    case SAVE_SEARCHED_ITEM: {
      try {
        const {
          searchedItem: { title },
        } = action;
        if (title) {
          saveSearchedItem(store, title);
        }
      } catch (error) {
        store.dispatch(storageWriteFailure(error));
      }
      break;
    }

    case GET_SEARCHED_ITEM: {
      const searchedItems = getSearchedItems(store);
      if (searchedItems) {
        store.dispatch(updateSearchedItems(searchedItems));
      }
      break;
    }

    case PAIRING_RESET: {
      setPairingMethod(PAIRING_METHOD.NONE);

      break;
    }

    default:
  }
};
